import { action, observable } from "mobx";
//import { trackPromise } from "react-promise-tracker";

import codeDataRestRepository from "../repository/CodeDataRestRepository";
import CodeDataSource, { CodeData } from "../source/CodeDataSource";

export interface RequestCode {
  KEY: string;
}

/**
 * 코드데이터 Store
 */
class CodeDataStore {
  @observable private _globalCodeDataSource: CodeDataSource;
  @observable private _codeDataSource: CodeDataSource;

  public constructor() {
    this._globalCodeDataSource = new CodeDataSource();
    this._codeDataSource = new CodeDataSource();
  }

  public get globalCodeDataSource(): CodeDataSource {
    return this._globalCodeDataSource;
  }

  @action
  public addGlobalCodeDataSource(_globalCodeDataMap: { [key: string]: CodeData[] }): void {
    this._globalCodeDataSource.append(_globalCodeDataMap);
  }

  public get codeDataSource(): CodeDataSource {
    return this._codeDataSource;
  }

  @action
  public addCodeDataSource(_codeDataMap: { [key: string]: CodeData[] }): void {
    this._codeDataSource.append(_codeDataMap);
  }

  public exists(key: string): boolean {
    return this._globalCodeDataSource.exists(key) || this._codeDataSource.exists(key);
  }

  public getCodeData(key: string, code: string): CodeData {
    if (this._globalCodeDataSource.exists(key)) {
      return this._globalCodeDataSource.getCodeData(key, code);
    }

    return this._codeDataSource.getCodeData(key, code);
  }

  public getCodeName(key: string, code?: string | boolean) {
    if (this._globalCodeDataSource.exists(key)) {
      return this._globalCodeDataSource.getCodeName(key, code);
    }

    return this._codeDataSource.getCodeName(key, code);
  }

  public get(key: string): CodeData[] {
    if (this._globalCodeDataSource.exists(key)) {
      return this._globalCodeDataSource.get(key);
    }

    return this._codeDataSource.get(key);
  }

  public getMap(key: string, filter?: (codeData: CodeData) => boolean): { [code: string]: any } {
    const map: { [code: string]: any } = {};

    this.get(key)
      .filter((codeData) => filter == null || filter(codeData) === true)
      .forEach((codeData) => (map[codeData.code] = codeData.name));

    return map;
  }

  private async readCodeDataSource(keys: string[]): Promise<{ [KEY: string]: CodeData[] } | null> {
    if (keys.length === 0) {
      return null;
    }

    const requestCodeList: RequestCode[] = [];

    keys.forEach((key: string) => {
      if (!this._globalCodeDataSource || !this._globalCodeDataSource.exists(key)) {
        requestCodeList.push({ KEY: key });
      }
    });

    if (requestCodeList.length === 0) {
      return null;
    }

    const result: { [KEY: string]: { list?: CodeData[]; error?: boolean; message?: string } } = await codeDataRestRepository.getMap(requestCodeList);

    if (result == null) {
      return null;
    }

    const codeDataMap: { [key: string]: CodeData[] } = {};

    for (const key in result) {
      const codeData = result[key];

      if (codeData.error === true) {
        console.error(`CodeData "${key}" read error => ${codeData.message}`);
      }

      if (codeData.list != null) {
        codeDataMap[key] = codeData.list;
      }
    }

    return codeDataMap;
  }

  /**
   * 조회 - Global
   *
   * @param keys
   */
  public async readGlobal(...keys: string[]): Promise<void> {
    const codeDataSource = await this.readCodeDataSource([...keys]);

    if (codeDataSource != null) {
      this.addGlobalCodeDataSource(codeDataSource);
    }
  }

  /**
   * 조회
   *
   * @param keys
   */
  public async read(...keys: string[]): Promise<void> {
    const codeDataSource = await this.readCodeDataSource([...keys]);

    if (codeDataSource != null) {
      this.addCodeDataSource(codeDataSource);
    }
  }

  /**
   * 조회 - Once
   *
   * @param key
   */
  public async readOnce(key: string): Promise<CodeData[]> {
    const codeDataSource = await this.readCodeDataSource([key]);

    if (codeDataSource != null && codeDataSource[key] != null) {
      return codeDataSource[key];
    }

    return [];
  }
}

const codeDataStore = new CodeDataStore();

export default codeDataStore;
